import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "myAllTab", "myTodoTab", "myStartedTab", "myCompletedTab",
    "mySourceAllTab", "mySourceOthersTab", "mySourceMineTab",
    "assignedAllTab", "assignedTodoTab", "assignedStartedTab", "assignedCompletedTab",
    "watchedAllTab", "watchedTodoTab", "watchedStartedTab", "watchedCompletedTab"
  ]

  // Card #4209: tracks the current "My Cards" filter + source so either one
  // can change the frame src without clobbering the other -- the original
  // updateMyFilter always rebuilt the URL from scratch with only `my_filter`,
  // which is fine when there's a single dimension but would silently drop
  // `my_source` the moment a status tab was clicked after the dropdown
  // changed it. Default is "others" (Assigned by Others), per Vanessa's spec.
  static values = { myFilter: { type: String, default: "started" }, mySource: { type: String, default: "others" } }

  connect() {
    // Card #4209: pick up the server-rendered active source tab (reflects
    // params[:my_source] on page load) so a saved/bookmarked link with
    // my_source=mine isn't clobbered by the Stimulus value default the
    // moment a status tab is clicked.
    if (this.hasMySourceMineTabTarget && this.mySourceMineTabTarget.classList.contains("text-blue-500")) {
      this.mySourceValue = "mine"
    } else if (this.hasMySourceAllTabTarget && this.mySourceAllTabTarget.classList.contains("text-blue-500")) {
      this.mySourceValue = "all"
    } else {
      this.mySourceValue = "others"
    }

    // Initialize with 'started' filter selected for all sections
    if (this.hasMyStartedTabTarget) {
      this.selectMyTab(this.myStartedTabTarget)
    }
    if (this.hasAssignedStartedTabTarget) {
      this.selectAssignedTab(this.assignedStartedTabTarget)
    }
    if (this.hasWatchedStartedTabTarget) {
      this.selectWatchedTab(this.watchedStartedTabTarget)
    }
  }

  // My Cards filters
  filterMyAll(event) {
    event.preventDefault()
    this.updateMyFilter('all', this.myAllTabTarget)
  }

  filterMyTodo(event) {
    event.preventDefault()
    this.updateMyFilter('todo', this.myTodoTabTarget)
  }

  filterMyStarted(event) {
    event.preventDefault()
    this.updateMyFilter('started', this.myStartedTabTarget)
  }

  filterMyCompleted(event) {
    event.preventDefault()
    this.updateMyFilter('completed', this.myCompletedTabTarget)
  }

  // Assigned to Others Cards filters
  filterAssignedAll(event) {
    event.preventDefault()
    this.updateAssignedFilter('all', this.assignedAllTabTarget)
  }

  filterAssignedTodo(event) {
    event.preventDefault()
    this.updateAssignedFilter('todo', this.assignedTodoTabTarget)
  }

  filterAssignedStarted(event) {
    event.preventDefault()
    this.updateAssignedFilter('started', this.assignedStartedTabTarget)
  }

  filterAssignedCompleted(event) {
    event.preventDefault()
    this.updateAssignedFilter('completed', this.assignedCompletedTabTarget)
  }

  // Watched Cards filters
  filterWatchedAll(event) {
    event.preventDefault()
    this.updateWatchedFilter('all', this.watchedAllTabTarget)
  }

  filterWatchedTodo(event) {
    event.preventDefault()
    this.updateWatchedFilter('todo', this.watchedTodoTabTarget)
  }

  filterWatchedStarted(event) {
    event.preventDefault()
    this.updateWatchedFilter('started', this.watchedStartedTabTarget)
  }

  filterWatchedCompleted(event) {
    event.preventDefault()
    this.updateWatchedFilter('completed', this.watchedCompletedTabTarget)
  }

  updateMyFilter(filter, selectedTab) {
    this.myFilterValue = filter
    this.reloadMyFrame()
    this.selectMyTab(selectedTab)
  }

  // Card #4209: "All" / "Assigned by Others" / "Self-Assigned" source tabs --
  // mutually exclusive, not additive. Defaults to "others". Visually
  // subordinate pill-tabs above the status tabs, per Vela's review of the
  // <select> this replaced.
  filterMySourceAll(event) {
    event.preventDefault()
    this.updateMySource("all", this.mySourceAllTabTarget)
  }

  filterMySourceOthers(event) {
    event.preventDefault()
    this.updateMySource("others", this.mySourceOthersTabTarget)
  }

  filterMySourceMine(event) {
    event.preventDefault()
    this.updateMySource("mine", this.mySourceMineTabTarget)
  }

  updateMySource(source, selectedTab) {
    this.mySourceValue = source
    this.reloadMyFrame()
    this.selectMySourceTab(selectedTab)
  }

  reloadMyFrame() {
    const frame = document.getElementById('my_cards_list')
    if (frame) {
      const url = new URL('/hub/my-cards/section/my', window.location.origin)
      const hubId = new URL(window.location.href).searchParams.get('hub_id')
      if (hubId) url.searchParams.set('hub_id', hubId)
      const programId = new URL(window.location.href).searchParams.get('program_id')
      if (programId) url.searchParams.set('program_id', programId)
      url.searchParams.set('my_filter', this.myFilterValue)
      url.searchParams.set('my_source', this.mySourceValue)
      frame.src = url.toString()
    }
  }

  updateAssignedFilter(filter, selectedTab) {
    const frame = document.getElementById('assigned_to_others_cards_list')
    if (frame) {
      const url = new URL('/hub/my-cards/section/assigned', window.location.origin)
      const hubId = new URL(window.location.href).searchParams.get('hub_id')
      if (hubId) url.searchParams.set('hub_id', hubId)
      const programId = new URL(window.location.href).searchParams.get('program_id')
      if (programId) url.searchParams.set('program_id', programId)
      url.searchParams.set('assigned_filter', filter)
      frame.src = url.toString()
    }
    this.selectAssignedTab(selectedTab)
  }

  updateWatchedFilter(filter, selectedTab) {
    const frame = document.getElementById('watched_cards_list')
    if (frame) {
      const url = new URL('/hub/my-cards/section/watched', window.location.origin)
      const hubId = new URL(window.location.href).searchParams.get('hub_id')
      if (hubId) url.searchParams.set('hub_id', hubId)
      const programId = new URL(window.location.href).searchParams.get('program_id')
      if (programId) url.searchParams.set('program_id', programId)
      url.searchParams.set('watched_filter', filter)
      frame.src = url.toString()
    }
    this.selectWatchedTab(selectedTab)
  }

  selectMyTab(tab) {
    // Remove active state from all My Cards tabs
    const myTabs = [this.myAllTabTarget, this.myTodoTabTarget, this.myStartedTabTarget, this.myCompletedTabTarget]
    myTabs.forEach(t => {
      t.classList.remove('border-blue-500', 'text-blue-600')
      t.classList.add('border-transparent', 'text-gray-500')
    })

    // Add active state to selected tab
    tab.classList.remove('border-transparent', 'text-gray-500')
    tab.classList.add('border-blue-500', 'text-blue-600')
  }

  selectMySourceTab(tab) {
    // Remove active state from all source tabs (subordinate styling --
    // lighter weight than the blue-500/gray-500 status tabs below).
    const sourceTabs = [this.mySourceAllTabTarget, this.mySourceOthersTabTarget, this.mySourceMineTabTarget]
    sourceTabs.forEach(t => {
      t.classList.remove('border-blue-400', 'text-blue-500')
      t.classList.add('border-transparent', 'text-gray-400')
    })

    // Add active state to selected tab
    tab.classList.remove('border-transparent', 'text-gray-400')
    tab.classList.add('border-blue-400', 'text-blue-500')
  }

  selectAssignedTab(tab) {
    // Remove active state from all Assigned to Others Cards tabs
    const assignedTabs = [this.assignedAllTabTarget, this.assignedTodoTabTarget, this.assignedStartedTabTarget, this.assignedCompletedTabTarget]
    assignedTabs.forEach(t => {
      t.classList.remove('border-blue-500', 'text-blue-600')
      t.classList.add('border-transparent', 'text-gray-500')
    })

    // Add active state to selected tab
    tab.classList.remove('border-transparent', 'text-gray-500')
    tab.classList.add('border-blue-500', 'text-blue-600')
  }

  selectWatchedTab(tab) {
    // Remove active state from all Watched Cards tabs
    const watchedTabs = [this.watchedAllTabTarget, this.watchedTodoTabTarget, this.watchedStartedTabTarget, this.watchedCompletedTabTarget]
    watchedTabs.forEach(t => {
      t.classList.remove('border-blue-500', 'text-blue-600')
      t.classList.add('border-transparent', 'text-gray-500')
    })

    // Add active state to selected tab
    tab.classList.remove('border-transparent', 'text-gray-500')
    tab.classList.add('border-blue-500', 'text-blue-600')
  }
}
